import type { TableName } from "./types";

/**
 * Per-table version counters and subscriptions, shared by every store implementation.
 * Writes call `touch()`; inside a transaction the touched tables are collected and
 * listeners run once at `commit()`. No polling, no second reactive layer.
 */
export class ChangeBus {
  private readonly versions = new Map<TableName, number>();
  private readonly listeners = new Set<{ tables: ReadonlySet<TableName>; fn: () => void }>();
  private depth = 0;
  private pending = new Set<TableName>();

  version(table: TableName): number {
    return this.versions.get(table) ?? 0;
  }

  subscribe(tables: readonly TableName[], fn: () => void): () => void {
    const entry = { tables: new Set(tables), fn };
    this.listeners.add(entry);
    return () => {
      this.listeners.delete(entry);
    };
  }

  touch(...tables: TableName[]): void {
    for (const t of tables) this.pending.add(t);
    if (this.depth === 0) this.flush();
  }

  begin(): void {
    this.depth++;
  }

  commit(): void {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth === 0) this.flush();
  }

  rollback(): void {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth === 0) this.pending.clear();
  }

  private flush(): void {
    if (this.pending.size === 0) return;
    const changed = this.pending;
    this.pending = new Set();
    for (const t of changed) this.versions.set(t, this.version(t) + 1);
    for (const l of [...this.listeners]) {
      for (const t of changed) {
        if (l.tables.has(t)) {
          l.fn();
          break;
        }
      }
    }
  }
}
